import { useCallback, useRef } from "react";

export type GovernedRefetchOptions<TData> = {
  /** Base poll interval in ms. Default 5000. */
  baseMs?: number;
  /** Cap for backed-off interval. Default 30000. */
  maxMs?: number;
  /** Multiplier applied per step past the backoff threshold. Default 2. */
  backoffFactor?: number;
  /** Consecutive unchanged polls before backoff starts. Default 3. */
  unchangedPollsBeforeBackoff?: number;
  /** Fraction of interval to jitter by, plus or minus. Default 0.2. */
  jitter?: number;
  /**
   * Optional selector returning the meaningful slice of data for change
   * detection. Falls back to the full data object.
   */
  compareKey?: (data: TData | undefined) => unknown;
};

/**
 * The slice of react-query's `Query` that a `refetchInterval` callback
 * receives as its second argument. Only the settled-fetch counters are needed,
 * so wrappers and tests can pass a lightweight fake.
 */
export type GovernedRefetchQuery = {
  state: {
    dataUpdateCount: number;
    errorUpdateCount: number;
  };
};

/**
 * The shape react-query expects for `refetchInterval`. Exported so callers can
 * hold one in a ref when the interval's owner needs the query's own data.
 */
export type GovernedRefetchFn<TData = unknown> = (
  data: TData | undefined,
  query?: GovernedRefetchQuery,
) => number | false;

export type GovernedRefetch<TData> = {
  /**
   * Pass directly as react-query's `refetchInterval` option. If you wrap it in
   * a lambda, forward BOTH arguments (`(data, query) => refetchInterval(data,
   * query)`) — without `query` the hook can't tell render-driven recomputes
   * from settled fetches and falls back to advancing backoff on every call.
   */
  refetchInterval: GovernedRefetchFn<TData>;
  resetBackoff: () => void;
};

const DEFAULTS = {
  baseMs: 5000,
  maxMs: 30000,
  backoffFactor: 2,
  unchangedPollsBeforeBackoff: 3,
  jitter: 0.2,
};

function hashData(data: unknown): string {
  if (data === undefined) return "__undef__";
  if (data === null) return "__null__";
  if (typeof data !== "object") return `${typeof data}:${String(data)}`;
  try {
    return JSON.stringify(data);
  } catch {
    return "__unhashable__";
  }
}

export function useGovernedRefetchInterval<TData>(
  options?: GovernedRefetchOptions<TData>,
): GovernedRefetch<TData> {
  const base = options?.baseMs ?? DEFAULTS.baseMs;
  const max = options?.maxMs ?? DEFAULTS.maxMs;
  const factor = options?.backoffFactor ?? DEFAULTS.backoffFactor;
  const threshold =
    options?.unchangedPollsBeforeBackoff ??
    DEFAULTS.unchangedPollsBeforeBackoff;
  const jitter = options?.jitter ?? DEFAULTS.jitter;

  const compareKeyRef = useRef(options?.compareKey);
  compareKeyRef.current = options?.compareKey;

  const lastKeyRef = useRef<string | undefined>(undefined);
  const unchangedCountRef = useRef(0);
  const lastFetchCountRef = useRef<number | null>(null);
  const cachedIntervalRef = useRef<number | null>(null);

  const resetBackoff = useCallback(() => {
    lastKeyRef.current = undefined;
    unchangedCountRef.current = 0;
    lastFetchCountRef.current = null;
    cachedIntervalRef.current = null;
  }, []);

  const refetchInterval = useCallback(
    (data: TData | undefined, query?: GovernedRefetchQuery): number | false => {
      // Intentionally do NOT short-circuit to `false` when the tab is hidden.
      // Pausing while hidden is delegated to react-query's `focusManager`
      // gating: its interval callback only fetches when the document is
      // focused/visible, so returning a number keeps the timer armed while
      // skipping the actual fetches.
      //
      // Returning `false` here would be a polling-killing bug. In react-query
      // v3, a `false` interval clears the underlying `setInterval` and is never
      // re-armed on its own. The interval is only recomputed after a fetch
      // settles (`updateTimers`) or when options change by reference
      // (`setOptions`). With our global `refetchOnWindowFocus: false`,
      // refocusing the tab triggers neither — so if a poll settled while hidden
      // and we returned `false`, polling would stay dead until an unrelated
      // re-render produced new options.
      //
      // Recomputes must also be idempotent between settled fetches. react-query
      // v3 re-evaluates this callback on EVERY render (`setOptions` →
      // `computeRefetchInterval`) and clears + re-arms the underlying
      // `setInterval` whenever the returned value differs from the current one.
      // Jitter made every recompute return a fresh value, so each render reset
      // the timer's countdown — and each recompute also counted as an
      // "unchanged poll", racing backoff to its cap. On pages that re-render
      // faster than the backed-off interval, the timer never fired and polling
      // starved permanently (e.g. the email PNG export modal spun forever on
      // the message editor, which re-renders every ~3s from session polling).
      // Guard: react-query passes the query as the second argument; only
      // advance backoff state and roll new jitter when a fetch has settled
      // since the last computation, and return the cached interval otherwise
      // so react-query sees an unchanged value and leaves the timer armed.
      const fetchCount = query
        ? query.state.dataUpdateCount + query.state.errorUpdateCount
        : null;
      if (
        fetchCount !== null &&
        fetchCount === lastFetchCountRef.current &&
        cachedIntervalRef.current !== null
      ) {
        return cachedIntervalRef.current;
      }
      lastFetchCountRef.current = fetchCount;

      const rawKey = compareKeyRef.current ? compareKeyRef.current(data) : data;
      const key = hashData(rawKey);

      if (lastKeyRef.current !== undefined && key === lastKeyRef.current) {
        unchangedCountRef.current += 1;
      } else {
        unchangedCountRef.current = 0;
      }
      lastKeyRef.current = key;

      const stepsPastThreshold = Math.max(
        0,
        unchangedCountRef.current - threshold,
      );
      const scaled = base * Math.pow(factor, stepsPastThreshold);
      const capped = Math.min(scaled, max);
      const jitterMultiplier = 1 + (Math.random() * 2 - 1) * jitter;
      const interval = Math.round(capped * jitterMultiplier);
      cachedIntervalRef.current = interval;
      return interval;
    },
    [base, max, factor, threshold, jitter],
  );

  return { refetchInterval, resetBackoff };
}
